import { Dispatch, FunctionComponent, JSX, SetStateAction, useCallback, useMemo } from 'react'
import { Daycare } from '@/types/Daycare'
import { Section } from '@/types/Section'
import styles from './sectionSelector.module.scss'
import { useTranslation } from 'react-i18next'
import SectionMenu, { MenuItem } from './SectionMenu'

interface SectionChecked {
    section: Section
    isCheck: boolean
}

interface WithoutSectionItem {
    daycareId: number
    isCheck: boolean
}

interface DaycareAndSections {
    daycare: Daycare
    sections: SectionChecked[]
    withoutSection: WithoutSectionItem
    isCheck: boolean
}

interface DaycareSectionSelectorProps {
    daycares: Daycare[]
    selectedDaycares: number[]
    setSelectedDaycares: Dispatch<SetStateAction<number[]>>
    selectedSections: number[]
    setSelectedSections: Dispatch<SetStateAction<number[]>>
    selectedWithoutSection: number[]
    setSelectedWithoutSection: Dispatch<SetStateAction<number[]>>
}

const DaycareSectionSelector: FunctionComponent<DaycareSectionSelectorProps> = ({
    daycares,
    selectedDaycares,
    setSelectedDaycares,
    selectedSections,
    setSelectedSections,
    selectedWithoutSection,
    setSelectedWithoutSection
}: DaycareSectionSelectorProps) => {
    const { t } = useTranslation()

    const daycareAndSections: DaycareAndSections[] = useMemo((): DaycareAndSections[] => {
        return daycares.map((daycare: Daycare): DaycareAndSections => {
            const isDaycareSelected: boolean = selectedDaycares.includes(daycare.id)
            const sections: SectionChecked[] = (daycare.sections ?? []).map(
                (section: Section): SectionChecked => ({
                    section,
                    isCheck: selectedSections.includes(section.id)
                })
            )
            const withoutSection: WithoutSectionItem = {
                daycareId: daycare.id,
                isCheck: selectedWithoutSection.includes(daycare.id)
            }
            return {
                daycare,
                sections,
                withoutSection,
                isCheck: isDaycareSelected
            }
        })
    }, [daycares, selectedDaycares, selectedSections, selectedWithoutSection])

    const handleDaycareChange: (daycareId: number) => void = useCallback(
        (daycareId: number): void => {
            const daycare: Daycare | undefined = daycares.find((d: Daycare): boolean => d.id === daycareId)
            if (!daycare) return

            const isCurrentlySelected: boolean = selectedDaycares.includes(daycareId)
            const daycareSectionIds: number[] = (daycare.sections ?? []).map((s: Section): number => s.id)

            if (isCurrentlySelected) {
                setSelectedDaycares((prev: number[]): number[] =>
                    prev.filter((id: number): boolean => id !== daycareId)
                )
                setSelectedSections((prev: number[]): number[] =>
                    prev.filter((id: number): boolean => !daycareSectionIds.includes(id))
                )
                setSelectedWithoutSection((prev: number[]): number[] =>
                    prev.filter((id: number): boolean => id !== daycareId)
                )
            } else {
                setSelectedDaycares((prev: number[]): number[] => [...prev, daycareId])
                setSelectedSections((prev: number[]): number[] => [
                    ...prev,
                    ...daycareSectionIds.filter((id: number): boolean => !prev.includes(id))
                ])
                setSelectedWithoutSection((prev: number[]): number[] => [...prev, daycareId])
            }
        },
        [daycares, selectedDaycares, setSelectedDaycares, setSelectedSections, setSelectedWithoutSection]
    )

    const handleSectionChange: (daycareId: number, sectionId: number) => void = useCallback(
        (daycareId: number, sectionId: number): void => {
            const daycare: Daycare | undefined = daycares.find((d: Daycare): boolean => d.id === daycareId)
            if (!daycare) return

            const isCurrentlySelected: boolean = selectedSections.includes(sectionId)
            const daycareSectionIds: number[] = (daycare.sections ?? []).map((s: Section): number => s.id)

            if (isCurrentlySelected) {
                const newSelectedSections: number[] = selectedSections.filter((id: number): boolean => id !== sectionId)
                setSelectedSections(newSelectedSections)

                const remainingSectionsForDaycare: number[] = newSelectedSections.filter((id: number): boolean =>
                    daycareSectionIds.includes(id)
                )
                const hasWithoutSection: boolean = selectedWithoutSection.includes(daycareId)
                if (remainingSectionsForDaycare.length === 0 && !hasWithoutSection) {
                    setSelectedDaycares((prev: number[]): number[] =>
                        prev.filter((id: number): boolean => id !== daycareId)
                    )
                }
            } else {
                setSelectedSections((prev: number[]): number[] => [...prev, sectionId])

                if (!selectedDaycares.includes(daycareId)) {
                    setSelectedDaycares((prev: number[]): number[] => [...prev, daycareId])
                }
            }
        },
        [daycares, selectedDaycares, selectedSections, selectedWithoutSection, setSelectedDaycares, setSelectedSections]
    )

    const handleWithoutSectionChange: (daycareId: number) => void = useCallback(
        (daycareId: number): void => {
            const daycare: Daycare | undefined = daycares.find((d: Daycare): boolean => d.id === daycareId)
            if (!daycare) return

            const isCurrentlySelected: boolean = selectedWithoutSection.includes(daycareId)
            const daycareSectionIds: number[] = (daycare.sections ?? []).map((s: Section): number => s.id)

            if (isCurrentlySelected) {
                setSelectedWithoutSection((prev: number[]): number[] =>
                    prev.filter((id: number): boolean => id !== daycareId)
                )

                const remainingSectionsForDaycare: number[] = selectedSections.filter((id: number): boolean =>
                    daycareSectionIds.includes(id)
                )
                if (remainingSectionsForDaycare.length === 0) {
                    setSelectedDaycares((prev: number[]): number[] =>
                        prev.filter((id: number): boolean => id !== daycareId)
                    )
                }
            } else {
                setSelectedWithoutSection((prev: number[]): number[] => [...prev, daycareId])

                if (!selectedDaycares.includes(daycareId)) {
                    setSelectedDaycares((prev: number[]): number[] => [...prev, daycareId])
                }
            }
        },
        [
            daycares,
            selectedDaycares,
            selectedSections,
            selectedWithoutSection,
            setSelectedDaycares,
            setSelectedWithoutSection
        ]
    )

    const WITHOUT_SECTION_ID: number = -1

    const sectionToMenuItem: (section: SectionChecked) => MenuItem = useCallback(
        (section: SectionChecked): MenuItem => ({
            id: section.section.id,
            label: section.section.name.toString(),
            isChecked: section.isCheck
        }),
        []
    )

    const getMenuItems: (daycareData: DaycareAndSections) => MenuItem[] = useCallback(
        (daycareData: DaycareAndSections): MenuItem[] => {
            const sectionItems: MenuItem[] = daycareData.sections.map(sectionToMenuItem)
            const withoutSectionItem: MenuItem = {
                id: WITHOUT_SECTION_ID,
                label: t('withoutSection'),
                isChecked: daycareData.withoutSection.isCheck
            }
            return [...sectionItems, withoutSectionItem]
        },
        [sectionToMenuItem, t]
    )

    const handleItemChange: (daycareId: number, itemId: number) => void = useCallback(
        (daycareId: number, itemId: number): void => {
            if (itemId === WITHOUT_SECTION_ID) {
                handleWithoutSectionChange(daycareId)
            } else {
                handleSectionChange(daycareId, itemId)
            }
        },
        [handleSectionChange, handleWithoutSectionChange]
    )

    const sortByLabelWithoutSectionLast: (a: MenuItem, b: MenuItem) => number = useCallback(
        (a: MenuItem, b: MenuItem): number => {
            if (a.id === WITHOUT_SECTION_ID) return 1
            if (b.id === WITHOUT_SECTION_ID) return -1
            return a.label.localeCompare(b.label)
        },
        []
    )

    return (
        <div className={styles.SectionSelector}>
            <p className={styles.title}>{t('structures')}</p>
            <div className={styles.groupSections}>
                {daycareAndSections.map(
                    (daycareData: DaycareAndSections): JSX.Element => (
                        <SectionMenu
                            key={daycareData.daycare.id}
                            sectionName={daycareData.daycare.name}
                            items={getMenuItems(daycareData)}
                            onSectionChange={(): void => handleDaycareChange(daycareData.daycare.id)}
                            onItemChange={(itemId: number): void => handleItemChange(daycareData.daycare.id, itemId)}
                            sortFn={sortByLabelWithoutSectionLast}
                        />
                    )
                )}
            </div>
        </div>
    )
}

export default DaycareSectionSelector
